// @ts-check
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button } from '@patternfly/react-core';

import PrimaryDualList from '../Common/PrimaryDualList';
import { parse, ContentType, getContentByType } from '../../utils/dtdl';
import useApiCall from '../../services/useApiCall';
import useAPI from '../../services/useAPI';
import { TableItem } from '../Common';
import { isItemInCollection } from '../Common/utils/collection';
import { useTranslation } from 'localization/useTranslation';

const availableItemsColumns = [
  {
    path: 'displayName',
    label: '',
    searchItem: true
  }
];

const selectedItemsColumns = [
  {
    path: 'displayName',
    label: '',
    searchItem: true,
    render: (displayName, item) => {
      return (
        <TableItem title={displayName} subTitle={_.get(item, 'modelId', '')} />
      );
    }
  }
];

const createModelsColumns = (T, clearModelsFilter) => ([
  {
    path: 'displayName',
    label: '',
    searchItem: true,
    render: (displayName, item) => {
      if (item['@context'] === 'clearAll') {
        return <Button onClick={clearModelsFilter}>{ T.modals.selectsignals.clearFilter }</Button>;
      }
      return (
        <TableItem title={displayName} subTitle={_.get(item, '@id', '')} />
      );
    }
  }
]);

const idIteratee = (item) => item.modelId + item.name;

/**
 * @typedef {object} SelectedContent
 * @property {string} modelId
 */

/**
 * @typedef {object} ModelContentSelectorProps
 * @property {SelectedContent[]} initialSelectedContents
 * @property {Function} onSelectionChanged
 * @property {ContentType} contentType
 * @property {boolean} [isSingleSelect]
 */

/**
 *
 * @param {ModelContentSelectorProps} props
 * @returns {JSX.Element}
 */
const ModelContentSelector = ({
  initialSelectedContents = [],
  onSelectionChanged,
  contentType = ContentType.TELEMETRY,
  isSingleSelect
}) => {
  const T = useTranslation();
  const api = useAPI();
  const [primaryItems, setPrimaryItems] = useState([]);
  const [effectiveItems, setEffectiveItems] = useState([]);
  const [fixedSelection] = useState(initialSelectedContents ?? []);
  const [loadModels, isLoadingModels] = useApiCall({
    apiCall: api.core.getModels,
    startAsLoading: true,
    onSuccess: useCallback((models) => {
      // modelData is array
      const dtdl = _.compact(_.map(models, model => model.modelData[0]));
      setPrimaryItems(dtdl);
      const usedItems = _.uniq(_.map(fixedSelection, content => content.modelId));
      const filteredList = _.filter(dtdl, item => {
        return _.some(usedItems, used => used === item['@id']);
      });
      (_.isEmpty(filteredList)) ? setEffectiveItems(dtdl) : setEffectiveItems([...filteredList, { '@context': 'clearAll', displayName: T.modals.selectsignals.clearFilter }]);
    }, [T.modals.selectsignals.clearFilter, fixedSelection]),
    onError: useCallback((error) => {
      // TODO: add alert error
      console.error('Error', error);
    }, [])
  });

  const emptyListText = useMemo(() => {
    if (contentType === ContentType.TELEMETRY) {
      return [
        undefined,
        T.modelContentSelector.emptySignals,
        T.modelContentSelector.emptySelection
      ];
    }
    return [
      undefined,
      T.modelContentSelector.emptyProperties,
      T.modelContentSelector.emptyPropertiesSelection
    ];
  }, [T.modelContentSelector.emptyProperties, T.modelContentSelector.emptyPropertiesSelection, T.modelContentSelector.emptySelection, T.modelContentSelector.emptySignals, contentType]);

  const clearModelsFilter = useCallback(() => {
    setEffectiveItems(primaryItems);
  }, [primaryItems]);

  useEffect(() => {
    loadModels();
  }, [loadModels]);

  const [secondaryItems, setSecondaryItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(initialSelectedContents);

  const onPrimarySelected = useCallback(item => {
    if (item) {
      const dtdlModel = parse(item, primaryItems);
      const content = _.map(getContentByType({ contentType: contentType, dtdlModel }), contentItem => {
        return {
          ...contentItem,
          modelId: item['@id']
        };
      });

      setSecondaryItems(_.reject(content, isItemInCollection(selectedItems, idIteratee)));
    } else {
      setSecondaryItems([]);
    }
  }, [primaryItems, contentType, selectedItems]);

  const modelsColumns = useMemo(() => createModelsColumns(T, clearModelsFilter), [T, clearModelsFilter]);
  const allColumns = useMemo(() => [modelsColumns, availableItemsColumns, selectedItemsColumns], [modelsColumns]);

  const _onSelectionChanged = useCallback(newSelectedItems => {
    setSelectedItems(newSelectedItems);
    onSelectionChanged?.(newSelectedItems);
  }, [onSelectionChanged]);

  return (
    <PrimaryDualList
      primaryItems={effectiveItems}
      onPrimarySelected={onPrimarySelected}
      idIteratee={idIteratee}
      sortProperty={'displayName'}
      columns={allColumns}
      secondaryItems={secondaryItems}
      selectedItems={selectedItems}
      onSelectionChanged={_onSelectionChanged}
      emptyListText={emptyListText}
      isLoadingModels={isLoadingModels}
      isSingleSelect={isSingleSelect}
    />
  );
};

ModelContentSelector.propTypes = {
  initialSelectedContents: PropTypes.array,
  onSelectionChanged: PropTypes.func
};
export default React.memo(ModelContentSelector);
